/**
 * Parse first and last name from full_name string.
 * First word = firstname, last word = lastname. Single word uses that for both.
 * @param {string} fullName - full_name or fullName from API
 * @returns {{ firstname: string, lastname: string }}
 */
export function parseNameFromFullName(fullName) {
    if (fullName == null || typeof fullName !== 'string') {
        return { firstname: '', lastname: '' };
    }
    const parts = fullName.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return { firstname: '', lastname: '' };
    if (parts.length === 1) return { firstname: parts[0], lastname: '' };
    return {
        firstname: parts[0],
        lastname: parts[parts.length - 1]
    };
}

/**
 * Ensure a user object has firstname and lastname, filling from full_name if missing.
 * Supports both full_name and fullName from backend. Does not mutate the original.
 * @param {object} user - user object from API (may have full_name, firstname, lastname)
 * @returns {object} new object with firstname/lastname set (and all other keys preserved)
 */
export function normalizeUser(user) {
    if (!user || typeof user !== 'object') return user;
    const fullName = user.full_name ?? user.fullName ?? '';
    const parsed = parseNameFromFullName(fullName);
    const firstname = (user.firstname != null && String(user.firstname).trim() !== '')
        ? String(user.firstname).trim()
        : parsed.firstname;
    const lastname = (user.lastname != null && String(user.lastname).trim() !== '')
        ? String(user.lastname).trim()
        : parsed.lastname;
    return { ...user, firstname, lastname };
}

/**
 * Normalize an array of user objects (e.g. students, preceptors, admins).
 * @param {array} users - array of user objects
 * @returns {array} new array with each user normalized
 */
export function normalizeUsers(users) {
    if (!Array.isArray(users)) return users;
    return users.map(normalizeUser);
}

/**
 * Normalize user fields inside schedule rotations (preceptor, copreceptor, students).
 * @param {array} rotations - array of rotation objects
 * @returns {array} new array with nested users normalized
 */
export function normalizeScheduleRotations(rotations) {
    if (!Array.isArray(rotations)) return rotations;
    return rotations.map(rot => ({
        ...rot,
        preceptor: rot.preceptor ? normalizeUser(rot.preceptor) : rot.preceptor,
        copreceptor: rot.copreceptor ? normalizeUser(rot.copreceptor) : rot.copreceptor,
        students: Array.isArray(rot.students) ? rot.students.map(normalizeUser) : rot.students
    }));
}

/**
 * Normalize a schedule data object (year -> rotations).
 * @param {object} scheduleData - { [year]: rotation[] }
 * @returns {object} new object with each year's rotations normalized
 */
export function normalizeArray(arr) {
    if (!Array.isArray(arr)) return [];
    return arr.map(item => item?._id ?? item).filter(Boolean);
}

export function normalizeScheduleData(scheduleData) {
    if (!scheduleData || typeof scheduleData !== 'object') return scheduleData || {};
    const out = {};
    for (const [year, rotations] of Object.entries(scheduleData)) {
        out[year] = normalizeScheduleRotations(Array.isArray(rotations) ? rotations : []);
    }
    return out;
}
